/**
 * Helper utilities for deployment cost calculations
 */

/**
 * Format currency values for display
 * @param {number} value - The value to format
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value) => {
    if (value >= 1000000) {
        return `$${(value / 1000000).toFixed(2)}M`;
    } else if (value >= 1000) {
        return `$${(value / 1000).toFixed(0)}K`;
    }
    return `$${value.toFixed(0)}`;
};

/**
 * Calculate growth multiplier for a given year
 * @param {number} year - Year number (1-5)
 * @param {number} yoyGrowthPercent - Year-over-year growth percentage
 * @returns {number} Growth multiplier
 */
export const getGrowthMultiplier = (year, yoyGrowthPercent) => {
    // Year 1 = 100% (multiplier = 1.00)
    // Year 2 = 100% * (1 + growth%)
    // Year 3 = 100% * (1 + growth%)^2
    // etc.
    return Math.pow(1 + (yoyGrowthPercent / 100), year - 1);
};

/**
 * Calculate total input and output tokens per session
 * @param {object} params - Session parameters
 * @returns {object} Object with totalInputTokens and totalOutputTokens
 */
export const calculateSessionTokens = ({
    requestsPerSession,
    tokensPerRequest,
    tokensInResponse,
    percentResponseInSubsequent
}) => {
    const r = requestsPerSession;
    const u = tokensPerRequest;
    const tir = tokensInResponse;
    const prs = percentResponseInSubsequent / 100;

    // Formula from Excel: (r*(u*(r+1)+tir*prs*(r-1)))/2
    const totalInputTokens = (r * (u * (r + 1) + tir * prs * (r - 1))) / 2;
    const totalOutputTokens = tir * r;

    return { totalInputTokens, totalOutputTokens };
};

/**
 * Calculate required throughput in tokens/sec
 * @param {object} params - Throughput parameters
 * @returns {number} Required throughput in tokens/sec
 */
export const calculateRequiredThroughput = ({
    totalTokensPerSession,
    sessionsPerMonth,
    operationsHoursPerDay
}) => {
    return (totalTokensPerSession * sessionsPerMonth) / (30 * operationsHoursPerDay * 3600);
};
